import {lazy, FC, Suspense} from 'react';
import {Route, Routes, Navigate, useLocation} from 'react-router-dom';
import {MasterLayout} from '../../_metronic/layout/MasterLayout';
import TopBarProgress from 'react-topbar-progress-indicator';
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils';
import useRouteRedirection from 'app/shared/hooks/useRouteRedirection';
import {OPS_OVERVIEW_ENABLED} from 'app/modules/operationalOverview/overviewFlags';
import {ChunkErrorBoundary} from './ChunkErrorBoundary';

const PrivateRoutes = () => {
  const {routeRedirectionByUser} = useRouteRedirection();

  const AccountPage = lazy(() => import('../modules/accounts/AccountsPage'));
  // const UsersPage = lazy(() => import('../modules/users/UsersPage'))

  const MechanicsPage = lazy(
    () => import('app/modules/mechanics/MechanicsPage')
  );
  const MembersPage = lazy(() => import('../modules/accounts/MembersPage'));
  const QuotesPage = lazy(() => import('../modules/quotes/QuotesPage'));
  const OrdersPage = lazy(() => import('../modules/orders/OrdersPage'));
  const AutopartsPage = lazy(() => import('../modules/catalog/CatalogPage'));
  const PurchasePage = lazy(() => import('../modules/purchases/PurchasePage'));
  const OperationSettingsPage = lazy(
    () => import('app/modules/operationSettings/OperationSettingsPage')
  );
  const CouponsPage = lazy(() => import('app/modules/coupons/CouponsPage'));
  const BannerCampaignPage = lazy(
    () => import('app/pages/BannerCampaign/BannerCampaignPage')
  );
  const DialerPage = lazy(() => import('app/modules/dialer/DialerPage'));
  const CancelledItemsPage = lazy(
    () => import('../modules/canceledItems/CancelledItemsPage')
  );
  const LogisticsPage = lazy(
    () => import('../modules/logistics/LogisticsPage')
  );
  const IncidentDeliveriesPage = lazy(
    () => import('../modules/incidentDeliveries/IncidentDeliveriesPage')
  );
  const AutopartProviderPage = lazy(
    () => import('app/pages/AutopartProvider/AutopartProviderPage')
  );
  const PaymentPage = lazy(() => import('../modules/finance/FinancePage'));
  const CategoryTreePage = lazy(
    () => import('app/pages/CategoryTree/CategoryTreePage')
  );
  // Tickets (módulo de produção).
  const TicketsEmbeddedPage = lazy(
    () => import('../modules/tickets/views/TicketsPanel')
  );
  // Visão operacional (fase 1: fixtures; fora do build de prod sem flag).
  const OperationalOverviewPage = lazy(
    () => import('../modules/operationalOverview/views/OperationalOverviewPage')
  );

  // Catálogo de padrões do Ops-Next (só admin: a própria página manda os
  // outros para o início).
  const PadroesPage = lazy(() => import('app/ops-next/catalog/PadroesPage'));

  return (
    <Routes>
      {/* Redirect to Dashboard after success login/registartion */}
      <Route
        path='auth/*'
        element={<Navigate to={routeRedirectionByUser('/quotes/workspace')} />}
      />
      <Route element={<MasterLayout />}>
        {/* Lazy Modules */}
        <Route
          path='/accounts/*'
          element={
            <SuspensedView>
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path='mechanics/*'
          element={
            <SuspensedView>
              <MechanicsPage />
            </SuspensedView>
          }
        />
        <Route
          path='members/*'
          element={
            <SuspensedView>
              <MembersPage />
            </SuspensedView>
          }
        />

        <Route
          path='orders/*'
          element={
            <SuspensedView>
              <OrdersPage />
            </SuspensedView>
          }
        />

        <Route
          path='catalog/*'
          element={
            <SuspensedView>
              <AutopartsPage />
            </SuspensedView>
          }
        />

        <Route
          path='deliveries/*'
          element={
            <SuspensedView>
              <LogisticsPage />
            </SuspensedView>
          }
        />

        <Route
          path='cancelled-items/*'
          element={
            <SuspensedView>
              <CancelledItemsPage />
            </SuspensedView>
          }
        />

        <Route
          path='providers/*'
          element={
            <SuspensedView>
              <AutopartProviderPage />
            </SuspensedView>
          }
        />

        <Route
          path='finance/*'
          element={
            <SuspensedView>
              <PaymentPage />
            </SuspensedView>
          }
        />

        <Route
          path='coupons/*'
          element={
            <SuspensedView>
              <CouponsPage />
            </SuspensedView>
          }
        />

        <Route
          path='operation-settings/*'
          element={
            <SuspensedView>
              <OperationSettingsPage />
            </SuspensedView>
          }
        />

        <Route
          path='banner-campaigns/*'
          element={
            <SuspensedView>
              <BannerCampaignPage />
            </SuspensedView>
          }
        />

        <Route
          path='dialer/*'
          element={
            <SuspensedView>
              <DialerPage />
            </SuspensedView>
          }
        />

        <Route
          path='category-tree/*'
          element={
            <SuspensedView>
              <CategoryTreePage />
            </SuspensedView>
          }
        />

        <Route
          path='tickets'
          element={
            <SuspensedView>
              <TicketsEmbeddedPage />
            </SuspensedView>
          }
        />
        <Route
          path='padroes'
          element={
            <SuspensedView>
              <PadroesPage />
            </SuspensedView>
          }
        />
        {OPS_OVERVIEW_ENABLED && (
          <Route
            path='visao-operacional'
            element={
              <SuspensedView>
                <OperationalOverviewPage />
              </SuspensedView>
            }
          />
        )}

        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
      <Route
        path='quotes/*'
        element={
          <SuspensedView>
            <QuotesPage />
          </SuspensedView>
        }
      />
      <Route
        path='purchases/*'
        element={
          <SuspensedView>
            <PurchasePage />
          </SuspensedView>
        }
      />
      <Route
        path='incident-deliveries/*'
        element={
          <SuspensedView>
            <IncidentDeliveriesPage />
          </SuspensedView>
        }
      />
    </Routes>
  );
};

const SuspensedView: FC = ({children}) => {
  const {pathname} = useLocation();
  const baseColor = getCSSVariableValue('--bs-primary');
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  });
  // Chunk da tela apagado por um deploy: recarrega em vez da página em branco
  return (
    <ChunkErrorBoundary resetKey={pathname}>
      <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
    </ChunkErrorBoundary>
  );
};

export {PrivateRoutes};
